import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, FileText, Sparkles, Building2, LayoutDashboard, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Contract } from '../../types';
import { fitLevel, FIT_TEXT } from '../../lib/fit';

interface CommandPaletteProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** Opportunities to fuzzy-search by title / NAICS / agency. */
  contracts?: Contract[];
  onSelectContract?: (contract: Contract) => void;
  onGenerateProposal?: () => void;
}

interface ActionItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  run: () => void;
}

/**
 * ⌘K / Ctrl-K command palette — keyboard-first fuzzy search over opportunities
 * (title / NAICS / agency) plus an action list. A real speed win at hundreds of
 * records. ↑↓ to move, ⏎ to act, ESC to close.
 */
export function CommandPalette({
  open,
  onOpen,
  onClose,
  contracts = [],
  onSelectContract,
  onGenerateProposal,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Global ⌘K / Ctrl-K toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        open ? onClose() : onOpen();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onOpen, onClose]);

  // Reset + focus on open.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filteredContracts = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = contracts.filter(Boolean);
    if (!q) return pool.slice(0, 6);
    return pool
      .filter((c) =>
        [c.title, c.naicsCode, c.agency]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      )
      .slice(0, 6);
  }, [contracts, query]);

  const actions: ActionItem[] = useMemo(
    () => [
      {
        id: 'gen',
        icon: <Sparkles className="h-4 w-4 text-bw-primary" />,
        label: 'Generate Proposal',
        shortcut: '⌘G',
        run: () => (onGenerateProposal ? onGenerateProposal() : navigate('/workspace')),
      },
      {
        id: 'profile',
        icon: <Building2 className="h-4 w-4 text-bw-primary" />,
        label: 'Open Company Profile',
        shortcut: '⌘P',
        run: () => navigate('/profile'),
      },
      {
        id: 'dashboard',
        icon: <LayoutDashboard className="h-4 w-4 text-bw-primary" />,
        label: 'Go to Dashboard',
        shortcut: '⌘D',
        run: () => navigate('/dashboard'),
      },
      {
        id: 'workspace',
        icon: <Briefcase className="h-4 w-4 text-bw-primary" />,
        label: 'Open Workspace',
        run: () => navigate('/workspace'),
      },
    ],
    [navigate, onGenerateProposal]
  );

  // Flat selectable list: contracts first, then actions.
  const flat = useMemo(
    () => [
      ...filteredContracts.map((c) => ({ kind: 'contract' as const, contract: c })),
      ...actions.map((a) => ({ kind: 'action' as const, action: a })),
    ],
    [filteredContracts, actions]
  );

  const runItem = useCallback(
    (idx: number) => {
      const item = flat[idx];
      if (!item) return;
      onClose();
      if (item.kind === 'contract') onSelectContract?.(item.contract);
      else item.action.run();
    },
    [flat, onClose, onSelectContract]
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runItem(active);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Keep active item in view.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-idx="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  let runningIndex = -1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] bg-neutral-900/45 dark:bg-black/70"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-[500px] max-w-[92vw] bg-bw-surface rounded-xl shadow-panel border border-bw-border overflow-hidden animate-cmd-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
      >
        {/* Search */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-bw-border">
          <Search className="h-4 w-4 text-bw-muted flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            placeholder="Search contracts, jump to page…"
            className="flex-1 bg-transparent border-0 outline-none text-sm text-bw-ink placeholder:text-bw-muted"
            aria-label="Search contracts and actions"
          />
          <kbd className="px-1.5 py-0.5 bg-bw-surface-subtle border border-bw-border rounded text-[10px] text-bw-muted font-mono">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="py-1.5 max-h-[52vh] overflow-y-auto">
          {/* Contracts */}
          {filteredContracts.length > 0 && (
            <>
              <div className="px-4 pt-1.5 pb-1.5 text-[10px] font-bold text-bw-muted tracking-[0.1em] uppercase font-mono">
                Contracts
              </div>
              {filteredContracts.map((c) => {
                runningIndex += 1;
                const idx = runningIndex;
                return (
                  <button
                    key={c.id}
                    data-cmd-idx={idx}
                    type="button"
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => runItem(idx)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-left ${
                      active === idx ? 'bg-bw-surface-subtle' : ''
                    }`}
                  >
                    <FileText className="h-3.5 w-3.5 text-bw-muted flex-shrink-0" />
                    <span className="text-[13px] text-bw-ink flex-1 truncate">{c.title}</span>
                    <span className={`font-mono text-[11.5px] font-medium ${FIT_TEXT[fitLevel(c.matchScore || 0)]}`}>
                      {c.matchScore || 0}
                    </span>
                  </button>
                );
              })}
              <div className="h-px bg-bw-border my-1.5" />
            </>
          )}

          {/* Actions */}
          <div className="px-4 pt-1.5 pb-1.5 text-[10px] font-bold text-bw-muted tracking-[0.1em] uppercase font-mono">
            Actions
          </div>
          {actions.map((a) => {
            runningIndex += 1;
            const idx = runningIndex;
            return (
              <button
                key={a.id}
                data-cmd-idx={idx}
                type="button"
                onMouseEnter={() => setActive(idx)}
                onClick={() => runItem(idx)}
                className={`w-full flex items-center gap-2.5 px-4 py-2 text-left ${
                  active === idx ? 'bg-bw-surface-subtle' : ''
                }`}
              >
                {a.icon}
                <span className="text-[13px] text-bw-ink flex-1">{a.label}</span>
                {a.shortcut && (
                  <kbd className="px-1.5 py-0.5 bg-bw-surface-subtle border border-bw-border rounded text-[10px] text-bw-muted font-mono">
                    {a.shortcut}
                  </kbd>
                )}
              </button>
            );
          })}

          {filteredContracts.length === 0 && query.trim() && (
            <div className="px-4 py-3 text-[12px] text-bw-muted">No contracts match “{query}”.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
